import { STATS_INTERVAL, type StatsInterval } from '@/lib/leads';
import type { TrendChartVariant } from '@bcecloud/portal-ui';
import type { TrendOverlay, TrendPoint, TrendSeries } from '@bcecloud/portal-ui';
import { trailingAverage } from '@/lib/trend';

/**
 * Shared plumbing for the Analytics pages (Leads, Bookings, Events): range
 * presets, Eastern-day windows, bucket labels, trailing-average windows,
 * money formatting, and freshness labels. The pages stay presentational.
 */

/** The API buckets days on Eastern time; the range pickers must agree. */
export const STATS_TIME_ZONE = 'America/New_York';

/** How often the pages re-ask for stats and sync status — the ingest cron runs every 5 minutes. */
export const LIVE_REFRESH_MS = 60_000;

/** `shortLabel` is what a phone shows, so a six-option range control still fits. */
export const RANGES = {
  '7d': { key: '7d', label: '7 days', shortLabel: '7d', days: 7, interval: STATS_INTERVAL.day },
  '30d': { key: '30d', label: '30 days', shortLabel: '30d', days: 30, interval: STATS_INTERVAL.day },
  '90d': { key: '90d', label: '90 days', shortLabel: '90d', days: 90, interval: STATS_INTERVAL.week },
  '12m': { key: '12m', label: '12 months', shortLabel: '1y', days: 365, interval: STATS_INTERVAL.month },
  '24m': { key: '24m', label: '24 months', shortLabel: '2y', days: 730, interval: STATS_INTERVAL.month },
  '36m': { key: '36m', label: '36 months', shortLabel: '3y', days: 1095, interval: STATS_INTERVAL.month },
} as const;

export type RangeKey = keyof typeof RANGES;

/**
 * Forward-looking presets for the event calendar: from today (inclusive)
 * out `days` ahead. The backward presets end at tomorrow; these start at
 * today, so "what is coming" and "what happened" meet cleanly.
 */
export const FORWARD_RANGES = {
  next30: { key: 'next30', label: 'Next 30 days', days: 30, interval: STATS_INTERVAL.day },
  next90: { key: 'next90', label: 'Next 90 days', days: 90, interval: STATS_INTERVAL.week },
  next12m: { key: 'next12m', label: 'Next 12 months', days: 365, interval: STATS_INTERVAL.month },
} as const;
export type ForwardRangeKey = keyof typeof FORWARD_RANGES;

/** [from, to) for a forward preset: today through `days` ahead (exclusive). */
export function forwardWindow(range: ForwardRangeKey, now: Date = new Date()): { from: string; to: string } {
  const from = zonedDay(now);
  const to = zonedDay(new Date(now.getTime() + FORWARD_RANGES[range].days * DAY_MS));
  return { from, to };
}

export const INTERVAL_LABELS: Readonly<Record<StatsInterval, string>> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
};

export const DAY_MS = 24 * 60 * 60 * 1000;

export const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const CHART_VARIANTS: readonly { key: TrendChartVariant; label: string }[] = [
  { key: 'bar', label: 'Bars' },
  { key: 'line', label: 'Line' },
];

/**
 * Trailing-average windows per bucket size — a short one that smooths the
 * weekday cycle and a long one that shows the season. Daily: a week and
 * four weeks (28 days keeps whole weeks, so weekends never skew it); weekly:
 * a month and a quarter; monthly: a quarter and a year.
 */
export const TREND_WINDOWS: Readonly<
  Record<StatsInterval, readonly { key: 'short' | 'long'; buckets: number; label: string }[]>
> = {
  day: [
    { key: 'short', buckets: 7, label: '7-day avg' },
    { key: 'long', buckets: 28, label: '28-day avg' },
  ],
  week: [
    { key: 'short', buckets: 4, label: '4-week avg' },
    { key: 'long', buckets: 13, label: '13-week avg' },
  ],
  month: [
    { key: 'short', buckets: 3, label: '3-month avg' },
    { key: 'long', buckets: 12, label: '12-month avg' },
  ],
};
export type TrendWindowKey = 'short' | 'long';

/** YYYY-MM-DD of an instant in the stats zone. */
function zonedDay(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: STATS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** [from, to) for a preset: `days` back through today (to = tomorrow, exclusive). */
export function rangeWindow(range: RangeKey, now: Date = new Date()): { from: string; to: string } {
  const to = zonedDay(new Date(now.getTime() + DAY_MS));
  const from = zonedDay(new Date(now.getTime() - (RANGES[range].days - 1) * DAY_MS));
  return { from, to };
}

/** Short label for a bucket key: "Aug 18", "Aug 18" (week of), "Aug 2026". */
export function periodLabel(period: string, interval: StatsInterval): string {
  if (interval === STATS_INTERVAL.month) {
    const [y, m] = period.split('-').map(Number) as [number, number];
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  const [y, m, d] = period.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** "3 min ago" / "just now" / "2 h ago" for the sync freshness line. */
export function agoLabel(iso: string | null): string {
  if (iso === null) return 'never';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString();
}

export function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Chart mapping ─────────────────────────────────────────────────────────

/** The shape every bucketed stats payload shares (leads, bookings, events). */
export interface BucketedStats {
  readonly range: { readonly interval: StatsInterval; readonly leadIn: number };
  readonly series: ReadonlyArray<{ readonly period: string }>;
  readonly segments: ReadonlyArray<{ readonly key: string; readonly values: readonly number[] }> | null;
  readonly yoy: readonly number[] | null;
}

/** The visible series (lead-in buckets dropped) as chart points. */
export function toTrendPoints<S extends BucketedStats>(
  stats: S,
  count: (point: S['series'][number]) => number,
  details?: (point: S['series'][number]) => string[],
): TrendPoint[] {
  return stats.series.slice(stats.range.leadIn).map((point) => ({
    key: point.period,
    label: periodLabel(point.period, stats.range.interval),
    value: count(point),
    details: details?.(point) ?? [],
  }));
}

/** The segmented series (lead-in dropped), or undefined when the chart is not split. */
export function toTrendSeries(stats: BucketedStats): TrendSeries[] | undefined {
  if (!stats.segments) return undefined;
  return stats.segments.map((segment) => ({
    key: segment.key,
    label: segment.key,
    values: segment.values.slice(stats.range.leadIn),
  }));
}

/** Last year's counts for the visible buckets as an overlay, when requested. */
export function toYoyOverlay(stats: BucketedStats): TrendOverlay | undefined {
  if (!stats.yoy) return undefined;
  return {
    key: 'yoy',
    label: 'Same period last year',
    values: stats.yoy.slice(stats.range.leadIn),
    tone: 1,
  };
}

/** Trailing averages over the full series (lead-in included), cut to the visible buckets. */
export function toTrendOverlays(
  stats: BucketedStats,
  counts: readonly number[],
  enabled: ReadonlySet<TrendWindowKey>,
): TrendOverlay[] {
  return TREND_WINDOWS[stats.range.interval]
    .filter((window) => enabled.has(window.key))
    .map((window) => ({
      key: window.key,
      label: window.label,
      values: trailingAverage(counts, window.buckets).slice(stats.range.leadIn),
      tone: window.key === 'short' ? 0 : 1,
    }));
}
